import { FC, Suspense } from 'react';
import { lazily } from 'react-lazily';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { Box } from '@bonusly/design-system';
import { assertPresence } from '@bonusly/runtime';

import { AdminAppLayout } from '../components/layout/AdminAppLayout';
import { AppRootLayout } from '../components/layout/AppRootLayout';
import { RequireAdmin } from '../components/routes/require-admin';
import { RouteError } from '../components/routes/RouteError';
import { MobileUnavailable } from '../components/routes/statuses/MobileUnavailable';
import { PageNotFound } from '../components/routes/statuses/PageNotFound';
import { AwardsRoot } from '../modules/awards/awards-root';
import { CreateAward } from '../modules/awards/create-award';
import { EditAward } from '../modules/awards/edit-award';
import { CelebrationsIndex } from '../modules/celebrations/celebrations-index';
import { CelebrationsRoot } from '../modules/celebrations/celebrations-root';
import { CreateCelebration } from '../modules/celebrations/create-celebration';
import { EditCelebration } from '../modules/celebrations/edit-celebration';
import { CreateIncentive } from '../modules/incentives/admin/create-incentive';
import { EditIncentive } from '../modules/incentives/admin/edit-incentive';
import { IncentivesArchivedIndex } from '../modules/incentives/admin/incentive-archived-index';
import { IncentivesIndex } from '../modules/incentives/admin/incentives-index';
import { IncentivesRoot } from '../modules/incentives/admin/incentives-root';
import { IncentiveDetails } from '../modules/incentives/redeem/components/incentive-details';
import { NominationsRoot } from '../modules/nominations/nominations-root';
import { loadAward } from '../queries/awards';
import { loadCelebration, loadCelebrationMetadata, loadInactiveCelebrations } from '../queries/celebrations';
import { loadCustomPropertiesUserGroups, loadUserGroups } from '../queries/groups';
import { loadIncentive } from '../queries/incentives';
import { loadAdminNominationPeriod } from '../queries/nominations';
import { loadSubscriptions } from '../queries/subscriptions';
import { PerformanceRecap } from './remote';

const { App: AdminCompany } = lazily(() => import('../modules/admin-company'));
const { App: AdminDashboard } = lazily(() => import('../modules/admin-dashboard/features/App'));
const { AdminGrowth } = lazily(() => import('../modules/admin-growth/components/admin-growth/admin-growth'));
const { IncentivesSummary } = lazily(() => import('../modules/incentives/summary/components/incentives-summary'));
const { App: AdminRewards } = lazily(() => import('../modules/admin-rewards/components/app/app'));
const { App: AdminPrograms } = lazily(() => import('../modules/admin-programs/components/app/app'));
const { App: Bookmarks } = lazily(() => import('../modules/bookmarks/components/app/app'));
const { App: BonusFeed } = lazily(() => import('../modules/bonus-feed/components/app/app'));
const { Incentives } = lazily(() => import('../modules/incentives/redeem/components/incentives'));
const { ReportSubscriptions } = lazily(() => import('../modules/admin-reports/report-subscriptions'));
const { App: Rewards } = lazily(() => import('../modules/rewards/components/App/App'));
const { Growth } = lazily(() => import('../modules/growth/components/growth/growth'));
const { CreateNominationPeriod } = lazily(() => import('../modules/nominations/create-nomination-period'));
const { EditNominationPeriod } = lazily(() => import('../modules/nominations/edit-nomination-period'));
const { AdminNominationPeriod } = lazily(() => import('../modules/nominations/admin-nomination-period'));
const { NominationPeriodsIndex } = lazily(() => import('../modules/nominations/nomination-periods-index'));
const { PublicNominationPeriod } = lazily(() => import('../modules/nominations/public-nomination-period'));
const { TeamManagement } = lazily(() => import('../modules/team-management/components/team-management/team-management'));
const { AwardsIndex } = lazily(() => import('../modules/awards/awards-index'));
const { AwardsArchivedIndex } = lazily(() => import('../modules/awards/awards-archived-index'));

const router = createBrowserRouter([
	{
		path: 'admin',
		element: <AdminAppLayout />,
		errorElement: <RouteError />,
		children: [
			{
				path: 'company/*',
				// TODO: Can we update the layout so we don't need to repeat <RouteError /> here?
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<AdminCompany />
					</Suspense>
				),
			},
			{
				path: 'dashboard/*',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<AdminDashboard />
					</Suspense>
				),
			},
			{
				path: 'growth/*',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<AdminGrowth />
					</Suspense>
				),
			},
			{
				path: 'incentives/*',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<IncentivesSummary />
					</Suspense>
				),
				children: [
					{
						path: 'summary',
						element: <IncentivesSummary />,
					},
				],
			},
			{
				path: 'subscriptions',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<ReportSubscriptions />
					</Suspense>
				),
				loader: () => loadSubscriptions(),
			},
			{
				path: 'programs/awards',
				element: <AwardsRoot />,
				errorElement: <RouteError />,
				children: [
					{
						path: '',
						element: (
							<Suspense>
								<AwardsIndex />
							</Suspense>
						),
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
					{
						path: 'new',
						element: (
							<Suspense>
								<CreateAward />
							</Suspense>
						),
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
					{
						path: ':id/edit',
						element: (
							<Suspense>
								<EditAward />
							</Suspense>
						),
						loader: ({ params }) => {
							assertPresence(params.id);
							return Promise.all([loadAward(params.id), loadCustomPropertiesUserGroups()]);
						},
					},
					{
						path: 'archived',
						element: (
							<Suspense>
								<AwardsArchivedIndex />
							</Suspense>
						),
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
				],
			},
			{
				path: 'programs/celebrations',
				element: <CelebrationsRoot />,
				errorElement: <RouteError />,
				children: [
					{
						path: '',
						element: (
							<Suspense>
								<CelebrationsIndex />
							</Suspense>
						),
						loader: () => {
							return loadInactiveCelebrations();
						},
					},
					{
						path: 'new/:type',
						element: (
							<Suspense>
								<CreateCelebration />
							</Suspense>
						),
						loader: ({ params }) => {
							assertPresence(params.type);
							return Promise.all([loadUserGroups(), loadCelebrationMetadata({ celebration_type: params.type })]);
						},
					},
					{
						path: ':id/edit',
						element: (
							<Suspense>
								<EditCelebration />
							</Suspense>
						),
						loader: ({ params }) => {
							assertPresence(params.id);
							return Promise.all([loadCelebration(params.id), loadUserGroups(), loadCelebrationMetadata({ celebration_id: params.id })]);
						},
					},
				],
			},
			{
				path: 'programs/nominations',
				element: <NominationsRoot />,
				errorElement: <RouteError />,
				children: [
					{
						path: '',
						element: (
							<Suspense>
								<NominationPeriodsIndex />
							</Suspense>
						),
					},
					{
						path: 'new',
						element: (
							<Suspense>
								<CreateNominationPeriod />
							</Suspense>
						),
						loader: () => loadUserGroups(),
					},
					{
						path: ':id',
						element: (
							<Suspense>
								<AdminNominationPeriod />
							</Suspense>
						),
						loader: ({ params }) => {
							assertPresence(params.id);
							return loadAdminNominationPeriod(params.id);
						},
					},
					{
						path: ':id/edit',
						element: (
							<Suspense>
								<EditNominationPeriod />
							</Suspense>
						),
						loader: ({ params }) => {
							assertPresence(params.id);
							return Promise.all([loadAdminNominationPeriod(params.id), loadUserGroups()]);
						},
					},
				],
			},
			{
				path: 'programs/incentives',
				element: <IncentivesRoot />,
				errorElement: <RouteError />,
				children: [
					{
						path: '',
						element: (
							<Suspense>
								<IncentivesIndex />
							</Suspense>
						),
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
					{
						path: 'new',
						element: (
							<Suspense>
								<CreateIncentive />
							</Suspense>
						),
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
					{
						path: ':id/edit',
						element: (
							<Suspense>
								<EditIncentive />
							</Suspense>
						),
						loader: ({ params }) => {
							assertPresence(params.id);
							return Promise.all([loadIncentive(params.id), loadCustomPropertiesUserGroups()]);
						},
					},
					{
						path: 'archived',
						element: (
							<Suspense>
								<IncentivesArchivedIndex />
							</Suspense>
						),
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
				],
			},
			{
				path: 'programs/*',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<AdminPrograms />
					</Suspense>
				),
			},
			{
				path: 'rewards/*',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<RequireAdmin require="rewards">
							<AdminRewards />
						</RequireAdmin>
					</Suspense>
				),
			},
			{
				path: '*',
				element: (
					<Box p={6}>
						<PageNotFound />
					</Box>
				),
			},
		],
	},
	// Print-friendly and shared pages do not have a navbar/layout
	{
		path: '/p',
		children: [
			{
				path: 'recap/*',
				errorElement: <RouteError />,
				element: <PerformanceRecap />,
			},
		],
	},
	{
		path: '/share',
		children: [
			{
				path: 'nominations/:id',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<PublicNominationPeriod />
					</Suspense>
				),
				loader: ({ params }) => {
					assertPresence(params.id);
					return loadAdminNominationPeriod(params.id);
				},
			},
		],
	},
	{
		path: '/',
		element: <AppRootLayout />,
		errorElement: <RouteError />,
		children: [
			{
				path: '/',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<BonusFeed />
					</Suspense>
				),
			},
			{
				path: '/invite',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<BonusFeed />
					</Suspense>
				),
			},
			{
				path: '/bonuses/:id',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<BonusFeed />
					</Suspense>
				),
			},
			{
				path: 'rewards/*',
				errorElement: <RouteError />,
				element: <Rewards />,
			},
			{
				path: 'bookmark',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<Bookmarks />
					</Suspense>
				),
			},
			{
				path: 'growth/*',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<Growth />
					</Suspense>
				),
			},
			{
				path: 'incentives/*',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<Incentives />
					</Suspense>
				),
				children: [
					{
						path: ':id',
						element: <IncentiveDetails />,
					},
				],
			},
			{
				path: 'team/*',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<TeamManagement />
					</Suspense>
				),
			},
			{
				path: 'mobile-unavailable',
				element: <MobileUnavailable />,
			},
			{
				path: '*',
				element: <PageNotFound />,
			},
		],
	},
]);

export const AppRouter: FC = () => <RouterProvider router={router} />;
